import { useState } from "react";
import { Control, Controller, FieldPath, FieldValues } from "react-hook-form";
import { TextInput, TextInputProps, View, Text, Pressable } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useCSSVariable } from "uniwind";

import { cn } from "@/shared/lib/utils";

type FormInputProps<T extends FieldValues> = {
  control: Control<T>;
  name: FieldPath<T>;
  label: string;
  placeholder?: string;
  prefix?: string;
  secureTextEntry?: boolean;
  showPasswordToggle?: boolean;
  autoCapitalize?: TextInputProps["autoCapitalize"];
  keyboardType?: TextInputProps["keyboardType"];
  autoComplete?: TextInputProps["autoComplete"];
  className?: string;
  containerClassName?: string;
};

export function FormInput<T extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  prefix,
  secureTextEntry,
  showPasswordToggle = false,
  autoCapitalize = "none",
  keyboardType = "default",
  autoComplete,
  className,
  containerClassName,
}: FormInputProps<T>) {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [mutedForeground] = useCSSVariable(["--color-muted-foreground"]) as [
    string,
  ];

  return (
    <Controller
      control={control}
      name={name}
      render={({
        field: { onChange, onBlur, value },
        fieldState: { error },
      }) => (
        <View className={cn("mb-4 w-full", className)}>
          <View
            className={cn(
              "bg-card-event border-b px-4 pt-3 pb-2",
              error ? "border-destructive" : "border-white",
              containerClassName
            )}
          >
            <Text className="text-muted-foreground mb-1 text-xs">{label}</Text>
            <View className="flex-row items-center">
              {prefix && (
                <Text className="text-muted-foreground mr-1 text-base leading-tight">
                  {prefix}
                </Text>
              )}
              <TextInput
                className={`text-foreground flex-1 text-base leading-tight outline-none ${
                  showPasswordToggle ? "pr-10" : ""
                }`}
                onBlur={onBlur}
                onChangeText={onChange}
                value={value}
                placeholder={placeholder}
                placeholderTextColor={mutedForeground}
                secureTextEntry={secureTextEntry && !isPasswordVisible}
                autoCapitalize={autoCapitalize}
                keyboardType={keyboardType}
                autoComplete={autoComplete}
              />
              {showPasswordToggle && secureTextEntry && (
                <Pressable
                  onPress={() => setIsPasswordVisible((prev) => !prev)}
                  className="absolute top-0 right-0 bottom-0 justify-center"
                  accessibilityLabel={
                    isPasswordVisible ? "Esconder senha" : "Mostrar senha"
                  }
                  accessibilityRole="button"
                >
                  <Ionicons
                    name={isPasswordVisible ? "eye-off-outline" : "eye-outline"}
                    size={20}
                    color={mutedForeground}
                  />
                </Pressable>
              )}
            </View>
          </View>
          {error && (
            <Text className="text-destructive mt-1 px-4 text-xs">
              {error.message}
            </Text>
          )}
        </View>
      )}
    />
  );
}
